import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import { useMeters, useDemandPeaks } from "../hooks/useMeters";
import {
  PageHeader,
  ChartCard,
  MeterSelect,
  DaysSelect,
  Spinner,
} from "../components/common";
import { fmt, CHART_COLORS } from "../utils/helpers";
import Layout from "../../../layout";
import "../reportAnalytics.css";

export default function DemandPeakPage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];
  const [meterId, setMeterId] = useState(meters[0] ?? "");
  const [days, setDays] = useState(90);

  const { data, isLoading } = useDemandPeaks(meterId || meters[0], days);
  const peaks = data?.data ?? [];

  const chartData = peaks.map((p: any) => ({
    period: p.period_label,
    md_kw: p.md_kw,
    md_kva: p.md_kva,
    limit: p.load_limit_value,
    util: p.utilisation_pct,
  }));

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Demand Peak Analysis"
          subtitle="Max demand KW/KVA vs. load limit per reset period"
        >
          <MeterSelect
            meters={meters}
            value={meterId || meters[0]}
            onChange={setMeterId}
          />
          <DaysSelect value={days} onChange={setDays} />
        </PageHeader>

        <ChartCard
          title="Max Demand KW per Period"
          subtitle="Red line = Load Limit"
        >
          {isLoading ? (
            <Spinner />
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.grid}
                />
                <XAxis
                  dataKey="period"
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return (
                      <div className="ra-tooltip">
                        <p className="text-slate-500 mb-1">{label}</p>
                        <p className="text-blue-600">
                          MD KW: {fmt.num(d.md_kw)} W
                        </p>
                        <p className="text-violet-600">
                          MD KVA: {fmt.num(d.md_kva)} VA
                        </p>
                        <p className="text-slate-500">
                          Utilisation: {fmt.pct(d.util)}
                        </p>
                      </div>
                    );
                  }}
                />
                {chartData[0] && (
                  <ReferenceLine
                    y={chartData[0].limit}
                    stroke={CHART_COLORS.red}
                    strokeDasharray="4 2"
                    label={{
                      value: `Limit ${chartData[0].limit}W`,
                      fill: CHART_COLORS.red,
                      fontSize: 10,
                    }}
                  />
                )}
                <Bar dataKey="md_kw" name="MD KW" radius={[4, 4, 0, 0]}>
                  {chartData.map((d: any, i: number) => (
                    <Cell
                      key={i}
                      fill={
                        d.util > 80
                          ? CHART_COLORS.red
                          : d.util > 50
                            ? CHART_COLORS.amber
                            : CHART_COLORS.blue
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <div className="ra-card mt-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">
            Period Summary
          </h2>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500 border-b border-slate-200">
                {[
                  "Period",
                  "MD KW (W)",
                  "MD KVA (VA)",
                  "Load Limit (W)",
                  "Utilisation",
                ].map((h) => (
                  <th key={h} className="text-left pb-2 pr-4 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {peaks.map((p: any, i: number) => (
                <tr
                  key={i}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="py-2 pr-4 text-slate-600">{p.period_label}</td>
                  <td className="py-2 pr-4 font-mono text-slate-800 font-semibold">
                    {fmt.num(p.md_kw)}
                  </td>
                  <td className="py-2 pr-4 font-mono text-violet-600">
                    {fmt.num(p.md_kva)}
                  </td>
                  <td className="py-2 pr-4 font-mono text-slate-500">
                    {fmt.num(p.load_limit_value)}
                  </td>
                  <td className="py-2">
                    <span
                      className={
                        p.utilisation_pct > 80
                          ? "ra-badge-red"
                          : p.utilisation_pct > 50
                            ? "ra-badge-yellow"
                            : "ra-badge-green"
                      }
                    >
                      {fmt.pct(p.utilisation_pct)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
